import type { MoodId } from "@wiseowl/design-system";
import { isApiError, type InsightDto } from "@wiseowl/shared";
import { api } from "./api.js";

/*
 * Device-local reflection store (doc 13 §3).
 *
 * Anonymous visitors keep exactly one reflection — name, mood, text, and
 * the AI insight — in localStorage. Nothing touches the server until
 * they sign up, at which point `claimAnonReflection()` replays it into
 * the authed API and clears the device. One reflection at a time, gated
 * by the user's local calendar day.
 */

const NAME_KEY = "wo.anon.name";
const REFLECTION_KEY = "wo.anon.reflection";
const TOKEN_KEY = "wo.token";

export interface AnonReflection {
  date: string; // local "YYYY-MM-DD"
  moodSlug: MoodId;
  moodAfter?: MoodId;
  name: string | null;
  text: string;
  insight: InsightDto;
  createdAt: string; // ISO
}

/** Local calendar day, "YYYY-MM-DD" — follows the user's own midnight. */
export const localDayKey = (d: Date = new Date()): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** A bearer token in storage means the visitor is signed in. */
export const hasToken = (): boolean => {
  try {
    return !!localStorage.getItem(TOKEN_KEY);
  } catch {
    return false;
  }
};

export const getName = (): string | null => {
  try {
    return localStorage.getItem(NAME_KEY);
  } catch {
    return null;
  }
};

export const setName = (name: string): void => {
  const trimmed = name.trim();
  if (!trimmed) return;
  try {
    localStorage.setItem(NAME_KEY, trimmed);
  } catch {
    /* private mode / quota — in-memory only */
  }
};

/** The stored reflection regardless of day (null if none / unparseable). */
export const getReflection = (): AnonReflection | null => {
  try {
    const raw = localStorage.getItem(REFLECTION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AnonReflection;
  } catch {
    return null;
  }
};

/** The stored reflection only if it belongs to today, else null. */
export const getTodayReflection = (): AnonReflection | null => {
  const r = getReflection();
  return r && r.date === localDayKey() ? r : null;
};

export const hasReflectedToday = (): boolean => getTodayReflection() !== null;

/** Overwrite the single device slot (only ever one anon reflection). */
export const saveReflection = (r: AnonReflection): void => {
  try {
    localStorage.setItem(REFLECTION_KEY, JSON.stringify(r));
  } catch {
    /* private mode / quota — the flow still ran in memory */
  }
};

export const clearAnon = (): void => {
  try {
    localStorage.removeItem(REFLECTION_KEY);
    localStorage.removeItem(NAME_KEY);
  } catch {
    /* no-op */
  }
};

/** Clear only today's reflection (keep the name) so the user can retake. */
export const clearReflectionOnly = (): void => {
  try {
    localStorage.removeItem(REFLECTION_KEY);
  } catch {
    /* no-op */
  }
};

/**
 * Promote the device reflection into the authed mood bank, then clear it.
 * Runs after a session token is set (see auth-context persistSession).
 * Best-effort and resilient: if the core POST fails we leave the data so
 * the next authed load retries. Insight + complete are non-blocking.
 */
export const claimAnonReflection = async (): Promise<void> => {
  const r = getReflection();
  if (!r) return;

  const created = await api<{ reflection: { id: string } }>("/reflections", {
    method: "POST",
    body: JSON.stringify({ practiceSlug: "daily-reset", moodSlug: r.moodSlug, body: r.text }),
  });
  if (isApiError(created)) return; // leave device data; retry next load

  const id = created.data.reflection.id;
  // Regenerate + persist the insight server-side (deterministic → matches).
  await api(`/reflections/${id}/insight`).catch(() => undefined);
  // Close out the reset if a mood-after was captured.
  if (r.moodAfter) {
    await api("/practices/daily-reset/complete", {
      method: "POST",
      body: JSON.stringify({ reflectionId: id, moodAfter: r.moodAfter }),
    }).catch(() => undefined);
  }
  clearAnon();
};
